import { useRollbar } from '@rollbar/react'
import React, { useState } from 'react'
import { useEffect } from 'react'
import Skeleton from 'react-loading-skeleton'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { InternalError } from 'common/errors'
import { useActions } from 'common/hooks/useActions'
import { fetchBlocksSuccess } from 'client/actions/blockActions'
import * as blocksApi from 'client/api/blockApi'
import useBlocks, { selectors as blocksSelectors } from 'client/hooks/useBlocks'
import { BlockCategories } from './block-categories'
import { BlocksListing } from './blocks-listing'
import { BlockCategoryType } from './types'

interface BlockCategoriesProps {
  pageType: PageTypeEnum
}

export const Blocks = ({ pageType }: BlockCategoriesProps) => {
  const [activeCategory, setActiveCategory] =
    useState<BlockCategoryType | null>(null)

  const { isFetched } = useBlockCategories()

  if (!isFetched) return <BlocksSkeleton />

  return activeCategory ? (
    <BlocksListing
      activeCategory={activeCategory}
      setActiveCategory={setActiveCategory}
    />
  ) : (
    <BlockCategories
      pageType={pageType}
      setActiveCategory={setActiveCategory}
    />
  )
}

function BlocksSkeleton() {
  return (
    <>
      {Array(8)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className="transition-[all_0.3s_cubic-bezier(0, 0, 0.8, 1)] flex cursor-pointer items-center justify-between py-2.5 text-L hover:underline"
          >
            <Skeleton width={i % 2 === 0 ? 150 : 200} />
          </div>
        ))}
    </>
  )
}

function useBlockCategories() {
  const isFetched = useBlocks(blocksSelectors.isFetched)
  const dispatchFetchBlocksSuccess = useActions(fetchBlocksSuccess)
  const rollbar = useRollbar()

  async function fetchBlocks() {
    try {
      const { data } = await blocksApi.fetchBlocks()
      dispatchFetchBlocksSuccess(data)
    } catch (error) {
      if (error instanceof InternalError) {
        rollbar.error(error)
      } else {
        // do nothing
      }
    }
  }

  useEffect(() => {
    if (!isFetched) {
      fetchBlocks()
    }
  }, [isFetched])

  return { isFetched }
}
